import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import { extractApiError, requestVolunteerLink } from '../api.js';
import { getVolunteerMeta, getVolunteerSession } from '../useActiveRoles.js';

const NEXT_KEY = 'sqseverac.auth.next';

/** Mémorise la page cible après clic sur le lien magique. */
export function rememberAuthNext(path) {
  try {
    sessionStorage.setItem(NEXT_KEY, path);
  } catch {
    /* ignore */
  }
}

export function takeAuthNext() {
  try {
    const v = sessionStorage.getItem(NEXT_KEY);
    sessionStorage.removeItem(NEXT_KEY);
    return v || null;
  } catch {
    return null;
  }
}

/**
 * Connexion staff par email (lien magique). Les jetons legacy restent en secours replié.
 *
 * @param {'admin'|'saisie'} props.audience
 * @param {(token: string) => void | Promise<void>} props.onLegacyToken — appelé si secours jeton
 * @param {string} [props.legacyError]
 */
export default function StaffEmailLogin({
  audience,
  title,
  description,
  onLegacyToken,
  legacyError,
  legacyLabel = 'Jeton technique',
}) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [legacyToken, setLegacyToken] = useState('');

  const session = getVolunteerSession();
  const meta = getVolunteerMeta();
  const hasSession = Boolean(session);
  const canAdmin = meta?.staff_role === 'admin' || meta?.is_super_admin;
  const canSaisie = canAdmin || meta?.staff_role === 'saisie';
  const missingRight =
    hasSession &&
    ((audience === 'admin' && !canAdmin) || (audience === 'saisie' && !canSaisie));

  async function handleSendLink(e) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSending(true);
    setError(null);
    setMsg(null);
    rememberAuthNext(audience === 'admin' ? '/admin' : '/saisie');
    try {
      const data = await requestVolunteerLink(trimmed);
      setMsg(
        data.message ||
          'Si votre compte est validé et autorisé, un lien vient d’être envoyé. Ouvrez-le sur cet appareil.',
      );
    } catch (err) {
      setError(extractApiError(err, 'Envoi impossible.'));
    } finally {
      setSending(false);
    }
  }

  async function handleLegacy(e) {
    e.preventDefault();
    const t = legacyToken.trim();
    if (!t) return;
    await onLegacyToken?.(t);
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h5">{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>

      {hasSession && !missingRight && (
        <Alert severity="info">
          Session déjà active ({meta?.email || 'bénévole'}). Rechargez la page si l’accès ne
          s’ouvre pas.
        </Alert>
      )}

      {missingRight && (
        <Alert severity="warning">
          Vous êtes connecté({meta?.email}) mais sans droit «{' '}
          {audience === 'admin' ? 'admin' : 'caisse'} ». Demandez au super-admin de vous
          l’attribuer, puis{' '}
          <Box component={RouterLink} to="/benevoles" sx={{ color: 'inherit', fontWeight: 600 }}>
            renvoyez-vous un lien
          </Box>
          .
        </Alert>
      )}

      <Box component="form" onSubmit={handleSendLink}>
        <Stack spacing={2}>
          <TextField
            label="Email"
            type="email"
            required
            fullWidth
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText="Même adresse que votre compte bénévole validé."
          />
          {error && <Alert severity="error">{error}</Alert>}
          {msg && <Alert severity="success">{msg}</Alert>}
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            startIcon={<SendIcon />}
            disabled={sending}
          >
            {sending ? 'Envoi…' : 'Recevoir mon lien de connexion'}
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary">
        Pas encore de compte ?{' '}
        <Box component={RouterLink} to="/benevoles" sx={{ color: 'primary.main' }}>
          Faire une demande bénévole
        </Box>
        {hasSession && (
          <>
            {' · '}
            <Box component={RouterLink} to="/benevoles/planning" sx={{ color: 'primary.main' }}>
              Planning
            </Box>
          </>
        )}
      </Typography>

      <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" color="text.secondary">
            Accès technique (jeton de secours)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="form" onSubmit={handleLegacy}>
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary">
                Réservé au dépannage. Le mode normal est le lien par email.
              </Typography>
              <TextField
                label={legacyLabel}
                type="password"
                fullWidth
                size="small"
                value={legacyToken}
                onChange={(e) => setLegacyToken(e.target.value)}
              />
              {legacyError && <Alert severity="error">{legacyError}</Alert>}
              <Button type="submit" variant="outlined" size="small">
                Se connecter avec le jeton
              </Button>
            </Stack>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
